import React, { useEffect, useState, useMemo, useCallback } from "react";

const TrainingConfigModal = ({
  title,
  isOpen,
  defaults,
  modelOptions,
  onSubmit,
  onClose,
  submitting = false,
  advancedOptions = {},
}) => {
  const mergedAdvanced = useMemo(
    () => ({
      enableStratify: false,
      enableSplitControls: false,
      enableBatchSize: true, // show batch size selector by default
      enableEarlyStopping: false,
      batchSizeOptions: [1, 2, 4, 6, 8, 16], // default options
      minTrainPercent: 50,
      splitLabels: { train: "Train", val: "Validation", test: "Test" },
      ...advancedOptions,
    }),
    [advancedOptions]
  );

  const computeInitialForm = useCallback(() => ({
    modelVariant: defaults?.modelVariant ?? modelOptions?.[0] ?? "",
    epochs: defaults?.epochs ?? 5,
    imgsz: defaults?.imgsz ?? 640,
    stratify: defaults?.stratify ?? mergedAdvanced.enableStratify,
    trainSplit: defaults?.trainSplit ?? 70,
    valSplit: defaults?.valSplit ?? 20,
    testSplit: defaults?.testSplit ?? 10,
    batchSize: defaults?.batchSize ?? mergedAdvanced.batchSizeOptions?.[0] ?? 16,
    enableEarlyStopping: defaults?.enableEarlyStopping ?? mergedAdvanced.enableEarlyStopping,
    patience: defaults?.patience ?? 50,
  }), [defaults, modelOptions, mergedAdvanced]);

  const getOptionsForVariant = useCallback(
    (variant) => {
      const baseOptions = mergedAdvanced.batchSizeOptions;
      if (!variant) return baseOptions;

      const normalizedVariant = variant
        .replace(/-(cls|det|seg)/gi, "")
        .replace(/\.pt.*$/i, ".pt");

      const isRestricted = /(m|s)\.pt$/i.test(normalizedVariant);
      const constrained = isRestricted ? baseOptions.filter((opt) => opt <= 4) : baseOptions;
      return constrained.length ? constrained : baseOptions;
    },
    [mergedAdvanced.batchSizeOptions],
  );

  const normalizeBatchValue = useCallback((options, batchValue) => {
    const numeric = Number(batchValue);
    if (options.includes(numeric)) {
      return numeric;
    }
    return options[0] ?? numeric ?? 1;
  }, []);

  const [form, setForm] = useState(computeInitialForm);
  const [batchOptions, setBatchOptions] = useState(mergedAdvanced.batchSizeOptions);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    const initial = computeInitialForm();
    setBatchOptions(mergedAdvanced.batchSizeOptions);
    setForm(initial);
    setErrors({});
  }, [computeInitialForm, isOpen, mergedAdvanced.batchSizeOptions]);

  const variantBatchOptions = useMemo(
    () => getOptionsForVariant(form.modelVariant || ""),
    [form.modelVariant, getOptionsForVariant],
  );

  useEffect(() => {
    setBatchOptions(variantBatchOptions);
    setForm((prev) => {
      const normalized = normalizeBatchValue(variantBatchOptions, prev.batchSize);
      if (String(normalized) === String(prev.batchSize)) {
        return prev;
      }
      return {
        ...prev,
        batchSize: normalized,
      };
    });
  }, [variantBatchOptions, normalizeBatchValue]);

  useEffect(() => {
    if (!isOpen) return;
    const initial = computeInitialForm();
    const nextOptions = getOptionsForVariant(initial.modelVariant);
    setBatchOptions(nextOptions);
    const updatedBatch = normalizeBatchValue(nextOptions, initial.batchSize);
    setForm({ ...initial, batchSize: updatedBatch });
    setErrors({});
  }, [computeInitialForm, getOptionsForVariant, isOpen, normalizeBatchValue]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "modelVariant") {
        const nextOptions = getOptionsForVariant(value);
        setBatchOptions(nextOptions);
        next.batchSize = normalizeBatchValue(nextOptions, next.batchSize);
      } else if (name === "batchSize") {
        const numericValue = Number(value);
        if (!batchOptions.includes(numericValue)) {
          next.batchSize = normalizeBatchValue(batchOptions, numericValue);
        }
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [name]: undefined, splitTotal: undefined }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const getSplitTotal = useCallback(
    (targetForm = form) =>
      ["trainSplit", "valSplit", "testSplit"].reduce(
        (acc, key) => acc + (Number(targetForm[key]) || 0),
        0
      ),
    [form]
  );

  const splitTotal = useMemo(
    () => (mergedAdvanced.enableSplitControls ? getSplitTotal() : 0),
    [getSplitTotal, mergedAdvanced.enableSplitControls]
  );

  const validateForm = useCallback(() => {
    const nextErrors = {};

    if (mergedAdvanced.enableSplitControls) {
      const train = Number(form.trainSplit) || 0;
      const val = Number(form.valSplit) || 0;
      const test = Number(form.testSplit) || 0;
      const total = train + val + test;

      if (Math.round(total) !== 100) {
        nextErrors.splitTotal = "Train/Val/Test ratios must add up to 100%.";
      }

      if (train < mergedAdvanced.minTrainPercent) {
        nextErrors.trainSplit = `Train ratio must be at least ${mergedAdvanced.minTrainPercent}%.`;
      }

      if (val <= 0) {
        nextErrors.valSplit = "Validation ratio must be greater than 0%.";
      }

      if (test < 0) {
        nextErrors.testSplit = "Test ratio cannot be negative.";
      }
    }

    if (mergedAdvanced.enableBatchSize) {
      const batch = Number(form.batchSize) || 0;
      if (batch <= 0) {
        nextErrors.batchSize = "Batch size must be an integer greater than zero.";
      }
    }

    if (mergedAdvanced.enableEarlyStopping && form.enableEarlyStopping) {
      const patience = Number(form.patience) || 0;
      if (patience <= 0) {
        nextErrors.patience = "Patience must be a positive integer.";
      }
      if (patience > 200) {
        nextErrors.patience = "Patience should not exceed 200 epochs.";
      }
    }

    return nextErrors;
  }, [form, mergedAdvanced]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitting) return;
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const normalized = {
      ...form,
      epochs: Math.min(Math.max(Number(form.epochs) || 1, 1), 100),
      imgsz: Math.max(Number(form.imgsz) || 1, 32),
    };

    if (mergedAdvanced.enableSplitControls) {
      normalized.trainSplit = Math.round(Number(form.trainSplit) || 0);
      normalized.valSplit = Math.round(Number(form.valSplit) || 0);
      normalized.testSplit = Math.round(Number(form.testSplit) || 0);
    } else {
      delete normalized.trainSplit;
      delete normalized.valSplit;
      delete normalized.testSplit;
    }

    if (mergedAdvanced.enableBatchSize) {
      normalized.batchSize = Math.max(1, Number(form.batchSize) || 1);
    } else {
      delete normalized.batchSize;
    }

    if (mergedAdvanced.enableStratify) {
      normalized.stratify = !!form.stratify;
    } else {
      delete normalized.stratify;
    }

    if (mergedAdvanced.enableEarlyStopping) {
      normalized.enableEarlyStopping = !!form.enableEarlyStopping;
      if (normalized.enableEarlyStopping) {
        normalized.patience = Math.max(1, Math.min(200, Number(form.patience) || 50));
      } else {
        delete normalized.patience;
      }
    } else {
      delete normalized.enableEarlyStopping;
      delete normalized.patience;
    }

    onSubmit?.(normalized);
  };

  return (
    <div
      className="modal fade show"
      tabIndex="-1"
      role="dialog"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label>YOLO Model</label>
                <select
                  className="form-control"
                  name="modelVariant"
                  value={form.modelVariant}
                  onChange={handleChange}
                >
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Epochs (1-100)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="epochs"
                    min={1}
                    max={100}
                    value={form.epochs}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>Image Size</label>
                  <input
                    type="number"
                    className="form-control"
                    name="imgsz"
                    min={32}
                    value={form.imgsz}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {mergedAdvanced.enableBatchSize && (
                <div className="form-group">
                  <label>Batch Size</label>
                  <select
                    className="form-control"
                    name="batchSize"
                    value={form.batchSize}
                    onChange={handleChange}
                  >
                    {batchOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  {errors.batchSize && <small className="text-danger">{errors.batchSize}</small>}
                </div>
              )}
              {mergedAdvanced.enableStratify && (
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="stratifyToggle"
                    name="stratify"
                    checked={!!form.stratify}
                    onChange={handleCheckboxChange}
                  />
                  <label className="form-check-label" htmlFor="stratifyToggle">
                    Stratified split maintain
                  </label>
                </div>
              )}
              {mergedAdvanced.enableEarlyStopping && (
                <>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="earlyStoppingToggle"
                      name="enableEarlyStopping"
                      checked={!!form.enableEarlyStopping}
                      onChange={handleCheckboxChange}
                    />
                    <label className="form-check-label" htmlFor="earlyStoppingToggle">
                      Enable Early Stopping
                    </label>
                  </div>
                  {form.enableEarlyStopping && (
                    <div className="form-group">
                      <label>Patience (epochs)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="patience"
                        min={1}
                        max={200}
                        value={form.patience}
                        onChange={handleChange}
                        placeholder="50"
                      />
                      <small className="text-muted">
                        Stop training if no improvement for this many epochs (1-200)
                      </small>
                      {errors.patience && <small className="text-danger d-block">{errors.patience}</small>}
                    </div>
                  )}
                </>
              )}
              {mergedAdvanced.enableSplitControls && (
                <>
                  <h6 className="mt-3">Dataset Split (%)</h6>
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label>{mergedAdvanced.splitLabels.train}</label>
                      <input
                        type="number"
                        className="form-control"
                        name="trainSplit"
                        min={0}
                        max={100}
                        value={form.trainSplit}
                        onChange={handleChange}
                      />
                      {errors.trainSplit && <small className="text-danger">{errors.trainSplit}</small>}
                    </div>
                    <div className="form-group col-md-4">
                      <label>{mergedAdvanced.splitLabels.val}</label>
                      <input
                        type="number"
                        className="form-control"
                        name="valSplit"
                        min={0}
                        max={100}
                        value={form.valSplit}
                        onChange={handleChange}
                      />
                      {errors.valSplit && <small className="text-danger">{errors.valSplit}</small>}
                    </div>
                    <div className="form-group col-md-4">
                      <label>{mergedAdvanced.splitLabels.test}</label>
                      <input
                        type="number"
                        className="form-control"
                        name="testSplit"
                        min={0}
                        max={100}
                        value={form.testSplit}
                        onChange={handleChange}
                      />
                      {errors.testSplit && <small className="text-danger">{errors.testSplit}</small>}
                    </div>
                  </div>
                  <div>
                    <small className={splitTotal === 100 ? "text-muted" : "text-danger"}>
                      Current total: {splitTotal}% (required: 100%)
                    </small>
                    {errors.splitTotal && (
                      <div className="text-danger small">{errors.splitTotal}</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" /> Starting...
                  </>
                ) : (
                  "Start Training"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingConfigModal;

